import React, { useState, useEffect } from 'react';
import { visitationService } from '../../services/visitation.service';
import type { Visitation } from '../../types/visitation';
import { LoadingSpinner } from '../common/LoadingSpinner';

interface VisitationHistoryProps {
  memberId: string;
}

const VisitationHistory: React.FC<VisitationHistoryProps> = ({ memberId }) => {
  const [visitations, setVisitations] = useState<Visitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchVisitations = async () => {
      if (!memberId) return;
      try {
        setLoading(true);
        const visitationList = await visitationService.getVisitationsForMember(memberId);
        setVisitations(visitationList);
      } catch (err) {
        console.error('심방 기록 조회 중 오류 발생:', err);
        if (err instanceof Error && err.message?.includes('requires an index')) {
          setError('Firestore 인덱스가 필요합니다. Firebase 콘솔에서 인덱스를 생성해주세요.');
        } else if (err instanceof Error && err.message?.includes('offline')) {
          setError('네트워크 연결이 불안정합니다. 오프라인 모드로 전환됩니다.');
        } else {
          setError('심방 기록을 불러오는 중 오류가 발생했습니다.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchVisitations();
  }, [memberId]);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <div className="text-red-500 text-center">{error}</div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold leading-7 text-gray-900">심방 기록</h3>
        <button className="no-print px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 text-sm">
          심방기록 추가
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">심방일</th>
              <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">심방자</th>
              <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">유형</th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">내용 요약</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {visitations.length > 0 ? (
              visitations.map((visitation) => (
                <tr key={visitation.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
                    {visitation.visitDate.toDate().toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
                    {Array.isArray(visitation.visitors) ? visitation.visitors.join(', ') : visitation.visitors}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">{visitation.type}</td>
                  <td className="px-6 py-4 text-sm text-gray-800">{visitation.notes}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={4} className="px-6 py-4 text-center text-sm text-gray-500">
                  심방 기록이 없습니다.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default VisitationHistory;
